import type { PayloadAction } from '@reduxjs/toolkit';

import { asyncThunkCreator, buildCreateSlice } from '@reduxjs/toolkit';

import { RootState } from '@app/store';
import { FEEDBACK_SUCCESS_TEXT, FEEDBACK_SUCCESS_TITLE } from '@shared/constants/text';
import { Metrics } from '@shared/types/enums/metrics.enum';
import { sendMetrics } from '@shared/utils/sendMetrics/sendMetrics';
import { sendFeedbackForm } from '@widgets/Feedback/api/sendFeedbackForm/sendFeedbackForm';

import { openAlert } from '../../../AlertModal';
import { CallModalStateType } from '../types/callModalState';

const createAppSlice = buildCreateSlice({
  creators: { asyncThunk: asyncThunkCreator }
});

const initialState: CallModalStateType = {
  openModal: false,
  name: '',
  phone: '',
  success: false,
  loading: false,
  errors: {
    phone: false,
    name: false
  }
};

export const callModalSlice = createAppSlice({
  name: 'callModal',
  initialState,
  reducers: (create) => ({
    openCallModal: create.reducer((state) => {
      state.openModal = true;
    }),
    closeCallModal: create.reducer((state) => {
      state.openModal = false;
    }),
    changeName: create.reducer((state, action: PayloadAction<string>) => {
      state.name = action.payload;
      state.errors.name = false;
    }),
    changePhone: create.reducer((state, action: PayloadAction<string>) => {
      state.phone = action.payload;
      state.errors.phone = false;
    }),
    updateErrors: create.reducer((state, action: PayloadAction<{ phone: boolean; name: boolean }>) => {
      state.errors = action.payload;
    }),
    sendData: create.asyncThunk(
      async (_: undefined, thunkAPI) => {
        const store = thunkAPI.getState() as RootState;
        const { name, phone } = store.callModal;

        let isValid = true;

        const errors = {
          name: false,
          phone: false
        };

        if (!name) {
          errors.name = true;
          isValid = false;
        }

        if (!phone) {
          errors.phone = true;
          isValid = false;
        }

        if (!isValid) {
          thunkAPI.dispatch(callModalSlice.actions.updateErrors(errors));
          return thunkAPI.rejectWithValue(errors);
        }

        const data = await sendFeedbackForm({ name, phone });

        if (data) {
          thunkAPI.dispatch(
            openAlert({
              data: {
                title: FEEDBACK_SUCCESS_TITLE,
                text: FEEDBACK_SUCCESS_TEXT
              }
            })
          );
        }

        return {
          data
        };
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state) => {
          state.success = true;
          state.name = '';
          state.phone = '';

          state.errors = initialState.errors;

          sendMetrics(Metrics.CALL_ME_FORM);
        },
        settled: (state) => {
          state.loading = false;
        }
      }
    )
  })
});

export const { changeName, changePhone, sendData, updateErrors, openCallModal, closeCallModal } =
  callModalSlice.actions;

export const callModalReducer = callModalSlice.reducer;
